import * as React from 'react';

import { captureException } from '@sentry/react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import {
  getEnrollingCourseActionId,
  getEnrollingCourseAssessmentActionId,
} from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayActivelyEnrollingContext';
import usePathwayAndCourseEnrollmentActions from 'bundles/enterprise-learning-path-description-page/hooks/usePathwayAndCourseEnrollmentActions';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

type Props = {
  isEnrolledInPathway: boolean;
  isEnrolledInCourse: boolean;
  isProgramMember: boolean;
  courseId: string;
  courseSlug: string;
  programId: string;
  redirectToAssessment?: boolean;
  assessmentId?: string;
  pathwayCollectionId?: string;
  pathwayId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  activateProgramAccessModal?: () => void;
  // isEnrollingCallback and disabled are used when multiple buttons that perform enrollment actions are present on the page.
  isEnrollingCallback?: (currentEnrollingAction: string | undefined) => void;
  disabled?: boolean;
};

// This button will handle enrolling the user into learning path and course if user is missing access.
// If redirecting to assessment, the button will fetch the assessment ID after user enrolls in course as enrollment is required for this item.
const EnterpriseSkillPathwayCourseActionButton = ({
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  pathwayCollectionId,
  pathwayId,
  courseId,
  programId,
  courseSlug,
  activateProgramAccessModal,
  programSlug,
  thirdPartyOrganizationSlug,
  isEnrollingCallback,
  disabled,
  assessmentId,
  redirectToAssessment,
}: Props) => {
  const navigate = useNavigate();
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const location = useLocation();
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const { executeEnrollments } = usePathwayAndCourseEnrollmentActions();
  const track = useTracker();

  React.useEffect(() => {
    if (isEnrolling && redirectToAssessment) {
      isEnrollingCallback?.(getEnrollingCourseAssessmentActionId(courseId));
    } else if (isEnrolling) {
      isEnrollingCallback?.(getEnrollingCourseActionId(courseId));
    } else {
      isEnrollingCallback?.(undefined);
    }
  }, [isEnrolling, isEnrollingCallback, courseId, redirectToAssessment]);

  const onActionSuccess = (redirectAssessmentId?: string) => {
    // Redirect on success, retain "isEnrolling" state as it continues to show loading state and prevent double click.
    if (redirectAssessmentId) {
      setLocation(`/learn/${courseSlug}/assignment-submission/${redirectAssessmentId}`);
    } else {
      setLocation(`/learn/${courseSlug}/home/welcome`);
    }
  };

  const onClick = async () => {
    track('click_button', {
      button: {
        name: redirectToAssessment
          ? 'skill_pathway_skill_area_take_assessment'
          : 'skill_pathway_skill_area_start_learning',
      },
      enterprise: {
        ...(thirdPartyOrganization?.slug ? { organizationSlug: thirdPartyOrganization?.slug } : {}),
        ...(program?.slug ? { programSlug: program?.slug } : {}),
      },
      product: {
        id: courseId,
        type: 'course',
      },
    });

    // Don't show enrolling state if already enrolled.
    if (!redirectToAssessment && isEnrolledInPathway && isEnrolledInCourse) {
      onActionSuccess();
      return;
    } else if (redirectToAssessment && assessmentId && isEnrolledInCourse && isEnrolledInPathway) {
      onActionSuccess(assessmentId);
      return;
    }

    if (!isAuthenticatedUser()) {
      navigate({ pathname: location.pathname, search: '?authMode=login' });
    } else if (!isProgramMember) {
      activateProgramAccessModal?.();
    } else {
      setIsEnrolling(true);

      try {
        await executeEnrollments({
          isEnrolledInPathway,
          isEnrolledInCourse,
          pathwayId,
          programId,
          courseId,
          pathwayCollectionId,
          programSlug,
          thirdPartyOrganizationSlug,
        });

        if (redirectToAssessment && assessmentId) {
          onActionSuccess(assessmentId);
        } else {
          onActionSuccess();
        }
      } catch (error) {
        captureException(error, {
          extra: { message: 'Error enrolling into skill pathway course' },
        });

        setIsEnrolling(false);
        // Since we don't have proper error states, reload page on failure to reset/refresh enrollment states (as we don't know what was a success or failure here).
        setLocation(location.pathname);
      }
    }
  };

  if (redirectToAssessment) {
    return (
      <Button onClick={onClick} disabled={disabled} size="small" loading={isEnrolling} variant="ghost">
        {_t('Start assessment')}
      </Button>
    );
  } else {
    return (
      <Button onClick={onClick} disabled={disabled} size="small" loading={isEnrolling} variant="secondary">
        {_t('Start learning')}
      </Button>
    );
  }
};

export default EnterpriseSkillPathwayCourseActionButton;
